import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { ProtectAction } from '../../../rbac';

interface ExportTableProps {
  data: any[];
  fileName: string;
  sheetName: string;
  columns: Array<{
    key: string;
    header: string;
    groupHeader?: string;
    accessor: (row: any) => string;
  }>;
  disabled?: boolean;
  className?: string;
  rPermission?: { [key: string]: boolean };
}

export type { ExportTableProps };

const ExportTable: React.FC<ExportTableProps> = ({
  data,
  fileName,
  sheetName,
  columns,
  disabled = false,
  className = '',
  rPermission,
}) => {
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setIsExportDropdownOpen(false);
      }
    };

    if (isExportDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportDropdownOpen]);

  // Export function
  const handleExportXLS = () => {
    try {
      if (!data?.length) {
        console.warn('No data available for export');
        setIsExportDropdownOpen(false);
        return;
      }

      const hasGroupedHeaders = columns.some((col) => col.groupHeader?.trim());

      let worksheet;
      if (hasGroupedHeaders) {
        // Create grouped export with header rows
        const grouped: Record<string, any[]> = {};
        const ungrouped: any[] = [];

        columns.forEach((col) => {
          if (col.groupHeader?.trim()) {
            grouped[col.groupHeader] = grouped[col.groupHeader] || [];
            grouped[col.groupHeader].push(col);
          } else {
            ungrouped.push(col);
          }
        });

        // Build header rows
        const groupHeaderRow: string[] = [];
        const subHeaderRow: string[] = [];
        const mergeRanges: any[] = [];
        let colIndex = 0;

        // Process columns in their original order
        columns.forEach((col) => {
          if (col.groupHeader?.trim()) {
            const groupName = col.groupHeader;
            const groupCols = grouped[groupName];
            const isFirstInGroup = groupCols[0] === col;

            if (isFirstInGroup && groupCols.length > 1) {
              mergeRanges.push({
                s: { r: 0, c: colIndex },
                e: { r: 0, c: colIndex + groupCols.length - 1 },
              });
            }
            groupHeaderRow.push(isFirstInGroup ? groupName : '');
          } else {
            groupHeaderRow.push('');
          }
          subHeaderRow.push(col.header);
          colIndex++;
        });

        // Build data rows
        const dataRows = data.map((row) => {
          const exportRow: string[] = [];
          columns.forEach((col) => exportRow.push(col.accessor(row)));
          return exportRow;
        });

        worksheet = XLSX.utils.aoa_to_sheet([groupHeaderRow, subHeaderRow, ...dataRows]);
        if (mergeRanges.length > 0) {
          worksheet['!merges'] = mergeRanges;
        }
      } else {
        // Simple export without grouping
        const exportData = data.map((row) => {
          const exportRow: Record<string, string> = {};
          columns.forEach((column) => {
            exportRow[column.header] = column.accessor(row);
          });
          return exportRow;
        });
        worksheet = XLSX.utils.json_to_sheet(exportData);
      }

      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0].replace(/-/g, '');
      const cleanFileName = fileName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
      const fullFileName = `${cleanFileName}-${formattedDate}.xlsx`;

      const cleanSheetName = sheetName
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 31);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, cleanSheetName);
      XLSX.writeFile(workbook, fullFileName);

      setIsExportDropdownOpen(false);
    } catch (error) {
      console.error('Error exporting to XLSX:', error);
      setIsExportDropdownOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={exportDropdownRef}>
      <ProtectAction canAccess={rPermission?.canExport}>
        <button
          className={`flex w-[133px] h-[40px] pt-[10px] pr-[16px] pb-[10px] pl-[16px] gap-[8px] items-center shrink-0 flex-nowrap rounded-[8px] border-solid border relative overflow-hidden table-button transition-all duration-200 ${
            disabled
              ? 'opacity-50 cursor-not-allowed bg-[#131f2e] border-[#4a5463] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]'
              : isExportDropdownOpen
                ? 'bg-[#131f2e] border-[#60bfda] '
                : 'bg-[#131f2e] border-[#4a5463]  hover:bg-[#1c2a3b] hover:border-[#5a6973] active:bg-[#0f1b28] focus:outline-none focus:ring-2 focus:ring-[#60bfda] focus:ring-opacity-50'
          }`}
          onClick={() => !disabled && setIsExportDropdownOpen(!isExportDropdownOpen)}
          disabled={disabled}
        >
          <span className='ico-down' />
          <span className="h-[20px] shrink-0 basis-auto font-['Mulish'] text-[14px] font-bold leading-[20px] text-[#f7f9f9] relative text-left whitespace-nowrap">
            Export
          </span>
          <span
            className={`down-chevron transition-transform duration-200 ${isExportDropdownOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </ProtectAction>
      {isExportDropdownOpen && !disabled && (
        <div
          className='absolute top-full left-0 mt-1 w-[160px] bg-[#131f2e] border border-[#4a5463] rounded-[8px] shadow-lg'
          style={{ zIndex: 999999, width: '133px' }}
        >
          <ul>
            <li
              className='px-4 py-3 text-[#f7f9f9] hover:bg-[#1c2a3b] cursor-pointer flex items-center gap-2'
              onClick={handleExportXLS}
            >
              <span className="font-['Mulish'] text-[14px]">Export XLS</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ExportTable;
